import React from "react";

import CallMergeIcon from "@mui/icons-material/CallMerge";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";

import SectionButton from "./SectionButton";
import SectionElements from "./SectionElements";
import Section from "../store/Section";

interface Props {
  section: Section;
  onMergeUp: () => void;
  onDelete: () => void;
}

const useStyles = makeStyles()((theme) => ({
  button: {
    borderWidth: "2px",
    fontWeight: "700",
    "&:hover": {
      borderWidth: "2px",
    },
  },
  cancelButton: {
    borderColor: colors.grey,
    color: colors.primary,
  },
  deleteButton: {
    borderColor: colors.error,
    color: colors.error,
    marginRight: "12px",
  },
  deleteIconColor: {
    color: colors.error,
  },
  deleteModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  deleteModalIconSize: {
    height: "60px",
    width: "60px",
  },
  iconButton: {
    border: `1.5px solid ${colors.borderColor}`,
    marginRight: "20px",
    marginBottom: "10px",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: colors.white,
    height: "auto",
    borderRadius: "16px",
  },
  deleteModalBody: {
    width: 400,
    padding: "40px",
  },
  modalContent: {
    fontWeight: "600",
    textAlign: "center",
    marginBottom: "24px",
  },
  modalTitle: {
    fontWeight: "700",
    marginBottom: "24px",
  },
  mergeIconColor: {
    color: "#FFB41E",
  },
  mergeIconModalSize: {
    height: "60px",
    width: "60px",
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(8),
  },
  sectionDeletedModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sectionDeletedModalIconSize: {
    height: "40px",
    width: "40px",
  },
  sectionDeletedModalBody: {
    width: "250px",
    paddingBlock: "20px",
    paddingInline: "66px",
  },
  sectionMergedModalBody: {
    width: "250px",
    paddingBlock: "20px",
  },
  sectionDeletedModalTitle: {
    fontWeight: "700",
  },
  section: {
    padding: theme.spacing(4),
    marginBottom: "20px",
    _backgroundColor: colors.white,
    get backgroundColor() {
      return this._backgroundColor;
    },
    set backgroundColor(value) {
      this._backgroundColor = value;
    },
  },
  sectionTitle: {
    borderRadius: "8px 8px 0px 0px",
    background: "#75A8C6",
    color: colors.white,
    padding: theme.spacing(3, 4),
  },
  sectionActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const SectionContainer: React.FC<Props> = observer(
  ({ section, onMergeUp, onDelete }) => {
    const { classes } = useStyles();

    const previousSection = section.form.sections[section.section - 2];

    const deleteDialog = useDialogState();
    const mergeDialog = useDialogState();

    const mergeUpSection = () => {
      mergeDialog.hide();
      section.mergeUp();
      onMergeUp();
    };

    const deleteSection = () => {
      deleteDialog.hide();
      section.remove();
      onDelete();
    };

    return (
      <div className={classes.sectionContainer} data-form-section>
        <div className={classes.sectionActions}>
          <span className={classes.sectionTitle}>
            <Typography>Section {section.section}</Typography>
          </span>
          <Box display="flex">
            {!section.isFirst && (
              <IconButton
                size="medium"
                className={classes.iconButton}
                onClick={mergeDialog.show}
              >
                <CallMergeIcon
                  fontSize="small"
                  color="action"
                  sx={{ color: colors.grey }}
                />
              </IconButton>
            )}
            {!section.isOnlySection && (
              <IconButton
                size="medium"
                className={classes.iconButton}
                onClick={deleteDialog.show}
              >
                <DeleteIcon
                  fontSize="small"
                  color="action"
                  sx={{ color: colors.grey }}
                />
              </IconButton>
            )}
          </Box>
        </div>
        <div className={classes.section}>
          <SectionElements section={section} />
        </div>
        <SectionButton section={section} />

        <Modal
          className={classes.deleteModal}
          open={mergeDialog.shown}
          onClose={mergeDialog.hide}
        >
          <Box className={`${classes.modalBody} ${classes.deleteModalBody}`}>
            <CallMergeIcon
              className={`${classes.mergeIconColor} ${classes.mergeIconModalSize}`}
            />
            <Typography variant="subtitle2" className={classes.modalContent}>
              Are you sure you want to merge this section to section{" "}
              {previousSection?.section}?
            </Typography>
            <Box>
              <Button
                variant="outlined"
                className={`${classes.button} ${classes.cancelButton}`}
                onClick={mergeDialog.hide}
                sx={{ marginRight: 4 }}
              >
                Cancel
              </Button>
              <Button className={`${classes.button}`} onClick={mergeUpSection}>
                Yes
              </Button>
            </Box>
          </Box>
        </Modal>
        <Modal
          className={classes.deleteModal}
          open={deleteDialog.shown}
          onClose={deleteDialog.hide}
        >
          <Box className={`${classes.modalBody} ${classes.deleteModalBody}`}>
            <DeleteIcon
              className={`${classes.deleteIconColor} ${classes.deleteModalIconSize}`}
            />
            <Typography variant="h5" className={classes.modalTitle}>
              Delete Section?
            </Typography>
            <Typography variant="subtitle2" className={classes.modalContent}>
              Deleting this section will be permanent and irreversible. Are you
              sure you want to delete this section?
            </Typography>
            <Box>
              <Button
                variant="outlined"
                className={`${classes.button} ${classes.deleteButton}`}
                onClick={deleteSection}
              >
                Delete
              </Button>
              <Button
                variant="outlined"
                className={`${classes.button} ${classes.cancelButton}`}
                onClick={deleteDialog.hide}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
      </div>
    );
  }
);

export default SectionContainer;
